<script lang="ts">
	import { page } from '$app/state';
  import { fade, fly, scale } from 'svelte/transition';
  import { sva, css } from '@agapi/ui/panda/css';
  import * as m from '$lib/parachute/messages';
  import { Button } from '@agapi/ui/button';
  import { onMount } from 'svelte';

  // Control visibility to trigger animations on mount
  let visible = false;

  // Easter egg messages from language files
  const easterEggMessages = [
    m.easterEgg1(),
    m.easterEgg2(),
    m.easterEgg3(),
    m.easterEgg4(),
    m.easterEgg5()
  ];

  const randomEasterEgg = easterEggMessages[Math.floor(Math.random() * easterEggMessages.length)];

  const errorStyles = sva({
    slots: ['root', 'title', 'subtitle', 'status', 'message', 'actions'],
    base: {
      root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100dvh - var(--navbarBottom))',
        width: '100vw',
        padding: '2rem',
        textAlign: 'center',
        fontFamily: 'var(--fonts-primary)',
        maxWidth: '1200px',
        margin: '0 auto'
      },
      title: {
        fontSize: { base: '3rem', md: '5rem' },
        fontWeight: '200',
        lineHeight: '1',
        margin: '0',
        color: 'var(--colors-primary-500)',
        letterSpacing: '-0.02em'
      },
      subtitle: {
        fontSize: { base: '1.5rem', md: '2.25rem' },
        fontWeight: '300',
        marginTop: '1rem',
        color: 'var(--colors-text-primary)',
        maxWidth: '700px'
      },
      status: {
        fontSize: { base: '12rem', md: '30rem' },
        fontWeight: '200',
        lineHeight: '1',
        color: 'primary.300',
        fontFamily: 'display',
        opacity: '0.1',
        position: 'absolute',
        zIndex: '-1',
        letterSpacing: '-0.05em'
      },
      message: {
        fontSize: { base: '1.25rem', md: '1.5rem' },
        fontWeight: '300',
        fontStyle: 'italic',
        marginTop: '2rem',
        color: 'var(--colors-text-secondary)',
        maxWidth: '600px'
      },
      actions: {
        display: 'flex',
        flexDirection: { base: 'column', md: 'row' },
        gap: '1rem',
        marginTop: '3rem',
        width: { base: '100%', md: 'auto' }
      }
    }
  });

  const classes = errorStyles.raw();

  // Set visible to true after component is mounted to trigger animations
  onMount(() => {
    // Small delay to ensure DOM is fully rendered
    setTimeout(() => {
      visible = true;
    }, 10);
  });
</script>

<div class={css(classes.root)}>
  <div class={css({ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' })}>
    {#if visible}
      <span class={css(classes.status)} in:scale={{duration: 600, delay: 100, start: 0.8}}>
        {page.status}
      </span>

      <h1 class={css(classes.title)} in:fly={{y: -20, duration: 400, delay: 200}}>
        {page.status === 404 ? m.notFoundTitle() : m.errorTitle()}
      </h1>

      <p class={css(classes.subtitle)} in:fly={{y: 20, duration: 400, delay: 300}}>
        {page.status === 404 ? m.notFoundSubtitle() : m.errorSubtitle()}
      </p>

      <p class={css(classes.message)} in:fade={{duration: 400, delay: 500}}>
        {randomEasterEgg}
      </p>
    {:else}
      <!-- Placeholder elements with visibility: hidden to prevent layout shift but keep them invisible -->
      <span class={css(classes.status)} style="visibility: hidden; opacity: 0;">
        {page.status}
      </span>
      <h1 class={css(classes.title)} style="visibility: hidden; opacity: 0;">
        {page.status === 404 ? m.notFoundTitle() : m.errorTitle()}
      </h1>
      <p class={css(classes.subtitle)} style="visibility: hidden; opacity: 0;">
        {page.status === 404 ? m.notFoundSubtitle() : m.errorSubtitle()}
      </p>
      <p class={css(classes.message)} style="visibility: hidden; opacity: 0;">
        {randomEasterEgg}
      </p>
    {/if}
  </div>

  {#if visible}
    <div class={css(classes.actions)} in:fly={{y: 30, duration: 500, delay: 600}}>
      <Button as="a" variant="primary" size="md" href="/">
        {m.returnToHomepage()}
      </Button>
      <Button as="a" variant="secondary" size="md" href={`mailto:${m.supportEmail()}`}>
        {m.contactSupport()}
      </Button>
    </div>
  {:else}
    <!-- Placeholder for buttons with visibility: hidden -->
    <div class={css(classes.actions)} style="visibility: hidden; opacity: 0;">
      <Button as="a" variant="primary" size="md" href="/">
        {m.returnToHomepage()}
      </Button>
      <Button as="a" variant="secondary" size="md" href={`mailto:${m.supportEmail()}`}>
        {m.contactSupport()}
      </Button>
    </div>
  {/if}
</div>
